import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isHandlerSite, OWNER_SITE_URL, HANDLER_SITE_URL } from '../config/siteMode';

// Site-wide defaults. Must stay in step with the static tags in public/index.html,
// which are what social crawlers actually read (they do not run JavaScript, so
// nothing set here ever reaches a WhatsApp or Facebook link preview).
const SITE_NAME = 'PetTrek';
const DEFAULT_TITLE = 'PetTrek | Trusted Dog Walking & Pet Care in South Africa';
const DEFAULT_DESCRIPTION =
  "Book verified pet handlers, pet sitters, groomers and trainers near you. Track your dog's walk live, chat with your handler, and pay securely.";

// The production origin of THIS build (owner vs handler site), used for the
// social-card image. og:image must be an absolute https URL — crawlers do not
// resolve relative image paths — so it is pinned to the deployed site URL
// rather than window.location.origin (which would be localhost in dev).
const SITE_URL = (isHandlerSite ? HANDLER_SITE_URL : OWNER_SITE_URL).replace(/\/+$/, '');
export const OG_IMAGE_URL = `${SITE_URL}/og-image.png`;

// Keep titles under ~60 characters and descriptions under ~160 so search
// results and browser tabs do not truncate them. Public routes (landing, auth,
// password reset, 404) are the ones whose cards can actually be shared;
// authed routes just need a sensible tab title and sane default tags.
const ROUTE_META = {
  '/': { title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION },
  '/auth': {
    title: `Sign in or create an account | ${SITE_NAME}`,
    description:
      'Sign in to PetTrek, or create an account as a pet owner or a verified handler.',
  },
  '/forgot-password': {
    title: `Forgot your password? | ${SITE_NAME}`,
    description: 'Request a password reset link for your PetTrek account.',
  },
  '/reset-password': {
    title: `Reset your password | ${SITE_NAME}`,
    description: 'Choose a new password for your PetTrek account.',
  },
  '/dashboard': { title: `Dashboard | ${SITE_NAME}` },
  '/owner/dashboard': {
    title: `My bookings | ${SITE_NAME}`,
    description: 'View upcoming walks, track services in progress, and rebook your favourite handlers.',
  },
  '/handler/dashboard': {
    title: `Handler dashboard | ${SITE_NAME}`,
    description: 'See nearby requests, manage active services, and track your earnings.',
  },
  '/handler/apply': {
    title: `Become a handler | ${SITE_NAME}`,
    description:
      'Apply to become a verified PetTrek handler — walk dogs, pet sit, and earn on your own schedule.',
  },
  '/pets': {
    title: `My pets | ${SITE_NAME}`,
    description: "Manage your pets' profiles, sizes, temperaments and health details.",
  },
  '/book-service': {
    title: `Book a service | ${SITE_NAME}`,
    description:
      'Book a dog walk, pet sitting, grooming or training session with a verified handler near you.',
  },
  '/profile': { title: `My profile | ${SITE_NAME}` },
  '/payments': {
    title: `Payments | ${SITE_NAME}`,
    description: 'Manage your payment methods and review your transaction history.',
  },
  '/wallet': {
    title: `Wallet | ${SITE_NAME}`,
    description: 'View your credit balance, top up, and review your credit history.',
  },
  '/payment/return': { title: `Confirming payment | ${SITE_NAME}` },
  '/admin/dashboard': { title: `Admin | ${SITE_NAME}` },
};

// Every registered route has an entry above, so an unmapped path is the 404
// page. It still gets a full, honest card rather than inheriting the previous
// page's title.
const NOT_FOUND_META = {
  title: `Page not found | ${SITE_NAME}`,
  description: DEFAULT_DESCRIPTION,
};

function setMeta(selector, attr, value) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.startsWith('link') ? 'link' : 'meta');
    const [, name] = selector.match(/\[(?:name|property|rel)="([^"]+)"\]/) || [];
    if (!name) return;
    if (selector.startsWith('link')) el.setAttribute('rel', name);
    else if (selector.includes('property=')) el.setAttribute('property', name);
    else el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

/**
 * Keeps the tab title, description, canonical URL and social-card tags
 * (Open Graph + Twitter) in step with the current route.
 *
 * This is primarily a usability win - readable tabs, meaningful history and
 * bookmark entries. Its SEO value is limited: Googlebot does render JavaScript
 * and will see these, but social crawlers (WhatsApp, Facebook, X) read only
 * the static tags in public/index.html. Genuine per-page social cards would
 * need server-side rendering or prerendering — tracked as remaining work.
 */
export default function RouteMeta() {
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = ROUTE_META[pathname] || NOT_FOUND_META;
    const title = meta.title || DEFAULT_TITLE;
    const description = meta.description || DEFAULT_DESCRIPTION;
    // Canonical strips the query string on purpose: /reset-password carries a
    // single-use token that must never end up in an index or a link preview.
    const canonicalUrl = window.location.origin + pathname;

    document.title = title;
    setMeta('meta[name="description"]', 'content', description);

    // Open Graph (WhatsApp, Facebook, LinkedIn)
    setMeta('meta[property="og:title"]', 'content', title);
    setMeta('meta[property="og:description"]', 'content', description);
    setMeta('meta[property="og:url"]', 'content', canonicalUrl);
    setMeta('meta[property="og:image"]', 'content', OG_IMAGE_URL);
    setMeta('meta[property="og:type"]', 'content', 'website');

    // X / Twitter
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', 'content', title);
    setMeta('meta[name="twitter:description"]', 'content', description);
    setMeta('meta[name="twitter:image"]', 'content', OG_IMAGE_URL);

    setMeta('link[rel="canonical"]', 'href', canonicalUrl);
  }, [pathname]);

  return null;
}
